import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, PackageOpen, ChevronDown, ChevronRight, FileText, Blinds } from "lucide-react";
import { apiGet, openObloaneDoc } from "@/lib/api";

export default function ObloanePage() {
  const [comenzi, setComenzi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [busyDoc, setBusyDoc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet("/comenzi");
      setComenzi((d.items || []).filter((c) => c.sursa === "obloane"));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDoc = async (comandaId, filename) => {
    setBusyDoc(`${comandaId}:${filename}`);
    try {
      await openObloaneDoc(comandaId, filename);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyDoc(null);
    }
  };

  return (
    <div data-testid="obloane-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Comenzi obloane</h1>
        <p className="text-sm text-slate-500 mt-1">
          Apasa pe o comanda ca sa vezi documentele. Comenzile se importa din fisierul .amelprod ca la Comenzi.
        </p>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : comenzi.length === 0 ? (
        <Card className="py-16 flex flex-col items-center text-slate-400" data-testid="obloane-empty-state">
          <PackageOpen className="h-10 w-10 mb-3" />
          <p className="text-sm">Nicio comanda obloane importata inca.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="obloane-list">
          {comenzi.map((c) => {
            const isOpen = openId === c.id;
            const docs = c.documente_obloane || [];
            return (
              <Card key={c.id} data-testid={`obloane-card-${c.nr_comanda}`} className="overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : c.id)}
                  data-testid={`obloane-toggle-${c.nr_comanda}`}
                  className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-slate-900 flex items-center justify-center shrink-0">
                      <Blinds className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900 truncate">{c.nr_comanda}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {c.client_nume || "(fara client)"}{c.client_localitate ? ` · ${c.client_localitate}` : ""}
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge variant="secondary" className="text-[11px]">{c.nr_pozitii} pozitii</Badge>
                        <Badge variant="outline" className="text-[11px]">{docs.length} documente</Badge>
                      </div>
                    </div>
                  </div>
                  {isOpen ? (
                    <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-slate-300 shrink-0" />
                  )}
                </button>

                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/60 p-4" data-testid={`obloane-docs-${c.nr_comanda}`}>
                    {docs.length === 0 ? (
                      <p className="text-sm text-slate-400">Fara documente in fisier.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {docs.map((d) => {
                          const busy = busyDoc === `${c.id}:${d.filename}`;
                          return (
                            <div
                              key={d.filename}
                              className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-md px-3 py-2"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="h-4 w-4 text-amber-600 shrink-0" />
                                <span className="text-sm font-medium text-slate-700 truncate">{d.label}</span>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={busy}
                                onClick={() => openDoc(c.id, d.filename)}
                                data-testid={`obloane-doc-${c.nr_comanda}-${d.key}`}
                              >
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Deschide"}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
